const AUTH_TOKEN = "authToken";
const REFRESH_TOKEN = "refreshToken";

import { ROUTE_LOGIN_OUT } from "@/constants/routes.constants";
import { TOKEN_TIME_BEFORE_EXPIRATION } from "@/constants/util.constants";
import { fetchJwt } from "@/models/api/authenticate.model";
import { clearAllLocFor } from "@/models/localForage/localForage.model";
import { setGlobalColorTheme } from "@/shadcn/lib/ThemeColors/theme-colors";
import { Tokens } from "@/types";
import { jwtDecode, JwtPayload } from "jwt-decode";
import Swal from "sweetalert2";

export function getLSAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN);
}

export function setLSAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN, token);
}

// #region getTokensLocalStorage
// Get refresh and auth tokens from local storage
export async function getTokensLocalStorage(): Promise<{
  refreshToken: string | null;
  authToken: string | null;
}> {
  const authToken = localStorage.getItem(AUTH_TOKEN);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  return { authToken, refreshToken };
}
// #endregion getTokensLocalStorage

export async function setTokensLocalStorage(tokens: Tokens) {
  try {
    const refreshToken = tokens.refreshToken;
    const authToken = tokens.authToken;
    localStorage.setItem(AUTH_TOKEN, authToken);
    localStorage.setItem(REFRESH_TOKEN, refreshToken);
  } catch (error) {
    console.error(`setTokensLocalStorage: ${error}`);
  }
}

export function utilLogOut() {
  utilClearAllLocalStorage();
  window.location.href = "/login";
}

export function utilClearAllLocalStorage() {
  localStorage.clear();
  clearAllLocFor();
}

export function utilClearLoginLocalStorage() {
  localStorage.removeItem(AUTH_TOKEN);
  clearAllLocFor();
}

export async function requestAndSetTokens(newJwt: string, config?: boolean) {
  try {
    const res = await fetchJwt(newJwt, config);
    if (res.rStatus === "success") {
      // Set Tokens
      if (res.data && res.data.authToken && res.data.refreshToken) {
        await setTokensLocalStorage({
          authToken: res.data.authToken,
          refreshToken: res.data.refreshToken,
        });
      }

      // Set Client Config
      if (res.data.config && res.data.config.cssVariables) {
        setGlobalColorTheme(res.data.config.cssVariables);
      }
    } else {
      console.log(res.message);
      Swal.fire({
        title: "Error!",
        text: "We were unable to authenticate you. Please login again",
        icon: "error",
      });
    }
  } catch (error) {
    console.error(error);
    Swal.fire({
      title: "Error!",
      text: "We were unable to authenticate you. Please login again",
      icon: "error",
    });
    utilLogOut();
  }
}

// #region isTokenAboutToExpire
// Check if the jwt is about to expire (Tentative Timing: 5 minutes)
export function isTokenAboutToExpire(token: string): boolean {
  try {
    const decoded: JwtPayload = jwtDecode(token);

    // log formatted date
    // console.log(
    //   new Date(decoded.exp * 1000).toLocaleString("UTC", {
    //     timeZone: "UTC",
    //   })
    // );

    // convert remaining time to milliseconds
    const remainingTimeMilliseconds = decoded.exp
      ? // ? Math.floor((decoded.exp * 1000 - Date.now()) / 1000)
        decoded.exp * 1000 - Date.now()
      : 0;

    // console.log("remainingTimeSeconds: " + remainingTimeMilliseconds / 1000);

    // if remaining remainingTimeMilliseconds is less than or equal to TOKEN_TIME_BEFORE_EXPIRATION, then return true
    if (remainingTimeMilliseconds <= TOKEN_TIME_BEFORE_EXPIRATION) {
      // console.log(
      //   "Token is about to expire in " +
      //     remainingTimeMilliseconds / 1000 +
      //     " seconds",
      // );
      return true;
    }

    // totalTimeRemaining : 00:00:00
    const totalTimeRemaining = `${remainingTimeMilliseconds / 1000} seconds`;
    if (!decoded.exp || decoded.exp < Date.now() / 1000) {
      console.log("Token expired " + totalTimeRemaining);
      return true;
    } else {
      // console.log("Token valid " + totalTimeRemaining);
      return false;
    }
  } catch (err) {
    console.error(err);
    return true;
  }
}
// #endregion isTokenAboutToExpire

// #region isTokenExpired
// Check if the jwt is not expired
export function isTokenExpired(token: string): boolean {
  try {
    // To decode and get the payload without specifying options
    const decoded: JwtPayload = jwtDecode(token);

    if (!decoded.exp) {
      return true;
    }

    // log formatted date
    // console.log(
    //   new Date(decoded.exp * 1000).toLocaleString("UTC", {
    //     timeZone: "UTC",
    //   })
    // );
    // remainingTimeHours, remainingTimeMinutes, remainingTimeSeconds

    const remainingTimeHours = Math.floor(
      (decoded.exp * 1000 - Date.now()) / (1000 * 60 * 60)
    );
    const remainingTimeMinutes = Math.floor(
      ((decoded.exp * 1000 - Date.now()) / (1000 * 60)) % 60
    );
    const remainingTimeSeconds = Math.floor(
      ((decoded.exp * 1000 - Date.now()) / 1000) % 60
    );
    // totalTimeRemaining : 00:00:00
    const totalTimeRemaining = `${remainingTimeHours}:${remainingTimeMinutes}:${remainingTimeSeconds}`;
    if (decoded.exp < Date.now() / 1000) {
      console.log("Token expired " + totalTimeRemaining);

      return true;
    } else {
      // console.log("Token valid " + totalTimeRemaining);
      return false;
    }
  } catch (err) {
    console.error(err);
    return true;
  }
}
// #endregion isTokenExpired

// #region refreshTokenCheckRedirect
// Checks if the refresh token exists in local storage, redirects accordingly
export function refreshTokenCheckRedirect() {
  const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN);
  console.log("storedRefreshToken: ", storedRefreshToken);

  // Check if the refresh token exists in local storage
  if (storedRefreshToken) {
    // console.log("Refresh token exists in local storage:", storedRefreshToken);

    // Check if the refresh token is expired
    if (isTokenExpired(storedRefreshToken)) {
      console.log("Refresh token expired");
      // Redirect to login page after 5 seconds
      window.location.href = ROUTE_LOGIN_OUT;
    } else {
      console.log("Refresh token is not expired");
      // Proceed with further actions if needed
      window.location.href = "/";
    }
  } else {
    console.log("Refresh token does not exist in local storage");
    // Redirect to login page or handle the absence of token

    window.location.href = ROUTE_LOGIN_OUT;
  }
}
// #endregion refreshTokenCheckRedirect
